import { useEffect, useMemo, useState } from "react";
import AppLayout from "@/components/AppLayout";
import Logo from "@/components/Logo";
import { api, API } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Trophy, Award, Printer, Settings } from "lucide-react";
import { Link } from "react-router-dom";

const CURRENT_YEAR = new Date().getFullYear();
const YEARS = Array.from({ length: 6 }, (_, i) => String(CURRENT_YEAR - 3 + i));

export default function AdminRanking() {
  const [year, setYear] = useState(String(CURRENT_YEAR));
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let active = true;
    (async () => {
      setLoading(true);
      setError(false);
      setItems([]);
      try {
        const { data } = await api.get("/ranking", { params: { year } });
        if (active) setItems(data);
      } catch (err) {
        if (!active) return;
        console.error("[admin ranking] load:", err);
        toast.error("Falha ao carregar ranking");
        setError(true);
      } finally { if (active) setLoading(false); }
    })();
    return () => { active = false; };
  }, [year]);

  const totalPoints = useMemo(
    () => items.reduce((s, a) => s + (Number(a.points) || 0), 0),
    [items]
  );
  const topScore = useMemo(
    () => items.reduce((highest, a) => Math.max(highest, Number(a.points) || 0), 0),
    [items]
  );
  const hasRankingData = !loading && !error && items.some(a => a.attendance_total > 0 || a.competitions > 0);

  const printPage = () => window.print();
  const issuedAt = new Date().toLocaleDateString("pt-BR");

  return (
    <AppLayout kind="admin">
      <div className="gsx-ranking-page">
        <header className="gsx-ranking-print-header" aria-label="Cabeçalho do relatório">
          <div className="gsx-ranking-print-brand">
            <Logo size={42} showText tone="dark" />
          </div>
          <div className="gsx-ranking-print-heading">
            <div className="gsx-ranking-print-kicker">Relatório institucional</div>
            <h1>Ranking Interno GSX</h1>
            <p>Classificação anual baseada em frequência e desempenho em competições.</p>
          </div>
          <div className="gsx-ranking-print-meta">
            <span className="gsx-ranking-year-seal">{year}</span>
            <time dateTime={new Date().toISOString()} className="gsx-ranking-print-date">Emitido em {issuedAt}</time>
          </div>
        </header>

        <div className="gsx-ranking-screen-header flex items-center justify-between mb-8 flex-wrap gap-3 print:mb-4">
        <div>
          <h1 className="text-3xl sm:text-4xl text-white font-semibold flex items-center gap-3">
            <Trophy className="w-8 h-8 text-amber-400" />
            Ranking Interno GSX — {year}
          </h1>
          <p className="text-slate-400 mt-1">Classificação anual baseada em frequência e desempenho em competições.</p>
        </div>
        <div className="flex gap-2 items-end flex-wrap print:hidden">
          <div>
            <div className="text-[11px] uppercase tracking-widest text-amber-300 mb-1">Ano</div>
            <Select value={year} onValueChange={setYear}>
              <SelectTrigger data-testid="rank-year-select" className="bg-[#0D2A52] border-amber-500/20 text-white w-32">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#071A33] text-white border-amber-500/20">
                {YEARS.map(y => <SelectItem key={y} value={y}>{y}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <Link to="/admin/ranking-config">
            <Button variant="outline" className="border-amber-500/30 bg-transparent text-amber-300 hover:bg-amber-500/10">
              <Settings className="w-4 h-4 mr-2" /> Configurar Pesos
            </Button>
          </Link>
          <Button onClick={printPage} data-testid="rank-print-btn"
            className="bg-amber-500 text-[#071A33] hover:bg-amber-400 font-semibold">
            <Printer className="w-4 h-4 mr-2" /> Imprimir / PDF
          </Button>
        </div>
      </div>

      {loading && <div className="text-slate-400 py-10 text-center">Carregando ranking…</div>}
      {!loading && error && <div role="alert" className="text-amber-200 py-10 text-center">Não foi possível consultar o ranking.</div>}
      {!loading && !error && !hasRankingData && (
        <div className="text-slate-400 py-10 text-center border border-dashed border-white/10 rounded-lg">
          Sem dados registrados em {year}. Registre chamadas e competições para gerar o ranking.
        </div>
      )}

      {hasRankingData && (
        <section className="gsx-ranking-summary" aria-label="Resumo do ranking">
          <div className="gsx-ranking-summary-card">
            <span>Atletas classificados</span>
            <strong>{items.length}</strong>
          </div>
          <div className="gsx-ranking-summary-card">
            <span>Pontos somados</span>
            <strong>{totalPoints}</strong>
          </div>
          <div className="gsx-ranking-summary-card">
            <span>Maior pontuação</span>
            <strong>{topScore}</strong>
          </div>
        </section>
      )}

      {hasRankingData && (
        <Card className="gsx-ranking-table-card bg-transparent gsx-card-dark overflow-hidden">
          <div className="gsx-ranking-table-scroll overflow-x-auto">
            <table className="gsx-ranking-table w-full text-sm">
              <thead className="bg-[#0D2A52] border-b border-amber-500/20 text-amber-300 text-xs uppercase tracking-widest">
                <tr>
                  <th className="p-3 text-left">Pos</th>
                  <th className="p-3 text-left">Atleta</th>
                  <th className="p-3 text-center">Total</th>
                  <th className="p-3 text-center">Freq. Pts</th>
                  <th className="p-3 text-center">Comp. Pts</th>
                  <th className="p-3 text-center">Comp.</th>
                  <th className="p-3 text-center">Freq. %</th>
                  <th className="p-3 text-center">Melhor col.</th>
                </tr>
              </thead>
              <tbody>
                {items.map((a, i) => {
                  const src = a.avatar_file_id
                    ? `${API}/athletes/${a.id}/avatar?v=${encodeURIComponent(a.avatar_uploaded_at || a.avatar_file_id)}`
                    : null;
                  const rowBg = i === 0 ? "bg-amber-500/10" :
                                i === 1 ? "bg-slate-400/10" :
                                i === 2 ? "bg-amber-700/10" : "";
                  return (
                    <tr key={a.id} className={`gsx-ranking-row ${i === 0 ? "gsx-ranking-row--gold" : i === 1 ? "gsx-ranking-row--silver" : i === 2 ? "gsx-ranking-row--bronze" : ""} border-b border-white/5 ${rowBg}`} data-testid={`rank-row-${a.id}`}>
                      <td className="p-3">
                        <div className={`gsx-ranking-position w-9 h-9 rounded-lg flex items-center justify-center font-mono font-bold text-sm ${
                          i === 0 ? "bg-amber-500 text-[#071A33]" :
                          i === 1 ? "bg-slate-300 text-[#071A33]" :
                          i === 2 ? "bg-amber-700 text-white" :
                          "bg-white/5 text-slate-300 border border-white/10"
                        }`}>{i + 1}</div>
                      </td>
                      <td className="p-3">
                        <div className="flex items-center gap-3">
                          <div className="gsx-ranking-avatar w-9 h-9 rounded-full border border-amber-500/30 bg-amber-500/10 overflow-hidden flex items-center justify-center shrink-0">
                            {src ? (
                              <img src={src} alt={a.name} className="w-full h-full object-cover" />
                            ) : (
                              <span className="text-amber-300 font-semibold text-xs">{(a.name||"?").slice(0,1)}</span>
                            )}
                          </div>
                          <div className="min-w-0">
                            <div className="text-white font-medium flex items-center gap-2">
                              {a.name}
                              {i < 3 && <Award aria-hidden="true" className="gsx-ranking-award w-3.5 h-3.5 text-amber-400" />}
                            </div>
                            <div className="text-xs text-slate-400">{a.category || "—"} • {a.class_name || "sem turma"}</div>
                          </div>
                        </div>
                      </td>
                      <td className="p-3 text-center font-mono text-amber-400 font-semibold text-lg" data-testid={`rank-points-${a.id}`}>{a.points}</td>
                      <td className="p-3 text-center font-mono text-slate-200">{a.frequency_points}</td>
                      <td className="p-3 text-center font-mono text-slate-200">{a.competition_points}</td>
                      <td className="p-3 text-center font-mono text-white">{a.competitions}</td>
                      <td className="p-3 text-center font-mono text-white">{a.frequency}%</td>
                      <td className="p-3 text-center font-mono text-amber-300">{a.best_placement ? `${a.best_placement}º` : "—"}</td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot className="bg-[#0D2A52]/50 text-xs">
                <tr>
                  <td colSpan={8} className="p-3 text-slate-400">
                    <span className="text-amber-300 font-semibold">{items.length}</span> atletas • Soma total de pontos: <span className="font-mono text-amber-400">{totalPoints}</span>
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </Card>
      )}
      <footer className="gsx-ranking-print-footer">
        <span>Grupo Santo de Xadrez · Ranking Interno {year}</span>
        <time dateTime={new Date().toISOString()}>Emitido em {issuedAt}</time>
      </footer>
      </div>
    </AppLayout>
  );
}
